/**
 * ComplianceChecklist — 10-domain checklist panel for Job Compliance tab.
 * Shows each compliance domain with status icon, score, and expandable details.
 * Also includes violations list and audit log.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, AlertTriangle, XCircle, Minus,
  ShieldCheck, ChevronDown, ChevronRight, Clock, User,
} from 'lucide-react';
import type { ComplianceData, DomainStatus } from './useCompliance';

interface ComplianceChecklistProps {
  data: ComplianceData;
  loading?: boolean;
  showAuditLog?: boolean;
  showViolations?: boolean;
}

const STATUS_ICONS = {
  pass: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50' },
  warn: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' },
  fail: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  na:   { icon: Minus, color: 'text-gray-400', bg: 'bg-gray-50' },
};

function DomainRow({ domain }: { domain: DomainStatus }) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_ICONS[domain.status];
  const Icon = cfg.icon;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="border-b last:border-0">
      <button
        className="flex items-center gap-3 w-full py-2.5 px-1 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className={`h-6 w-6 rounded-full flex items-center justify-center ${cfg.bg}`}>
          <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium">{domain.label}</span>
        </div>
        <span className={`text-xs font-bold ${cfg.color}`}>
          {domain.status === 'na' ? '—' : `${domain.score}%`}
        </span>
        <Chevron className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      {open && domain.details && (
        <div className="px-10 pb-2 text-xs text-muted-foreground">
          {domain.details}
        </div>
      )}
    </div>
  );
}

export function ComplianceChecklist({ data, loading, showViolations = true, showAuditLog = true }: ComplianceChecklistProps) {
  if (loading) {
    return (
      <div className="space-y-2 animate-pulse">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-10 bg-muted/30 rounded" />
        ))}
      </div>
    );
  }

  const passCount = data.domains.filter((d) => d.status === 'pass').length;
  const totalActive = data.domains.filter((d) => d.status !== 'na').length;

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Compliance Check</span>
        </div>
        <Badge variant="outline" className="text-xs">
          {passCount}/{totalActive} domains passed
        </Badge>
      </div>

      {/* Domain checklist */}
      <Card>
        <CardContent className="p-0">
          {data.domains.map((d) => (
            <DomainRow key={d.domain} domain={d} />
          ))}
        </CardContent>
      </Card>

      {/* Violations */}
      {showViolations && data.violations.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              Issues ({data.violations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.violations.map((v, i) => (
              <div key={i} className="flex items-start gap-2 px-4 py-2 border-b last:border-0 text-xs">
                <XCircle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{v.field}</span>
                  <span className="text-muted-foreground"> — {v.message}</span>
                </div>
                <Badge variant="outline" className="text-[9px] shrink-0">
                  {v.severity}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Audit log */}
      {showAuditLog && data.auditLog.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-primary" />
              Audit Trail
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.auditLog.slice(0, 20).map((entry, i) => (
              <div key={i} className="flex items-start gap-2 px-4 py-2 border-b last:border-0 text-xs">
                <User className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{entry.action}</span>
                  <span className="text-muted-foreground"> · {entry.domain} · {entry.actor}</span>
                  {entry.detail && <p className="text-muted-foreground mt-0.5">{entry.detail}</p>}
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
                  {entry.timestamp}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
